import { useState, useEffect } from 'react';
import { ArrowLeft, ArrowRight, Info } from 'lucide-react';
import type { WSJFItem } from '../types';

interface ScoringViewProps {
  item: WSJFItem;
  itemIndex: number;
  totalItems: number;
  phase: 'costOfDelay' | 'jobDuration';
  onScoreUpdate: (field: 'costOfDelay' | 'jobDuration', value: number | null) => void;
  onNext: () => void;
  onBack: () => void;
  canProceed: boolean;
  progress: number;
}

export function ScoringView({
  item,
  itemIndex,
  totalItems,
  phase,
  onScoreUpdate,
  onNext,
  onBack,
  canProceed,
  progress,
}: ScoringViewProps) {
  const [value, setValue] = useState<string>(
    phase === 'costOfDelay' 
      ? (item.costOfDelay?.toString() || '')
      : (item.jobDuration?.toString() || '')
  );

  useEffect(() => {
    setValue(
      phase === 'costOfDelay' 
        ? (item.costOfDelay?.toString() || '')
        : (item.jobDuration?.toString() || '')
    );
  }, [item, phase]);

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setValue(inputValue);
    
    if (inputValue === '') {
      onScoreUpdate(phase, null);
    } else {
      const numValue = parseFloat(inputValue);
      if (!isNaN(numValue) && numValue > 0) {
        onScoreUpdate(phase, numValue);
      } else {
        onScoreUpdate(phase, null);
      }
    }
  };

  const currentValue = phase === 'costOfDelay' ? item.costOfDelay : item.jobDuration;
  const label = phase === 'costOfDelay' ? 'Cost of Delay (CoD)' : 'Job Duration';
  const description = phase === 'costOfDelay' 
    ? 'The economic impact or value lost per unit of time if this job is delayed. Higher values indicate more urgent work.'
    : 'The estimated time or effort required to complete this job. Lower values indicate smaller, faster jobs.';

  return (
    <div className="max-w-3xl mx-auto">
      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-2">
          <span className="text-lg font-semibold text-[var(--text-pri)]">
            Item {itemIndex + 1} of {totalItems}
          </span>
          <span className="text-sm font-medium text-[var(--text-sec)]">
            {Math.round(progress)}% complete
          </span>
        </div>
        <div className="w-full bg-[var(--hover-tint)] rounded-full h-3">
          <div
            className="bg-[var(--slate)] h-3 rounded-full transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Main Scoring Card */}
      <div className="bg-[var(--bg-raised)] shadow-md border border-[var(--rule)] p-8 mb-6">
        <div className="mb-6">
          <h2 className="text-3xl font-bold text-[var(--text-pri)] mb-2">
            {item.name}
          </h2>
          <div className="flex items-center gap-2 text-sm text-[var(--text-sec)]">
            <span className="px-3 py-1 bg-[var(--slate-dim)] text-[var(--text-sec)] rounded-full font-medium">
              {phase === 'costOfDelay' ? 'Step 1 of 2' : 'Step 2 of 2'}
            </span>
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-lg font-semibold text-[var(--text-pri)] mb-2">
            {label}
          </label>
          <p className="text-sm text-[var(--text-sec)] mb-4">
            {description}
          </p>
          
          <div className="relative">
            <input
              type="number"
              value={value}
              onChange={handleValueChange}
              placeholder="Enter a number..."
              min="0.1"
              step="0.1"
              className="w-full px-4 py-3 text-2xl border-2 border-[var(--rule)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              autoFocus
            />
            {currentValue !== null && currentValue > 0 && (
              <div className="mt-2 text-sm text-[var(--green)] dark:text-[var(--text-ter)] font-medium">
                ✓ Value set: {currentValue}
              </div>
            )}
          </div>
        </div>

        {/* Info Box */}
        <div className="p-4 bg-[var(--slate-dim)] border border-[var(--slate)] mb-6">
          <div className="flex items-start gap-2">
            <Info className="h-5 w-5 text-[var(--text-ter)] flex-shrink-0 mt-0.5" />
            <div className="text-sm text-[var(--text-sec)]">
              <strong>Tip:</strong> {phase === 'costOfDelay' 
                ? 'Use relative values (e.g., 1-10 scale) or actual economic impact. Higher CoD means more valuable/urgent work.'
                : 'Use consistent units (e.g., story points, days, hours). Smaller duration means faster delivery.'}
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <button
            onClick={onBack}
            className="flex items-center gap-2 px-4 py-2 text-[var(--text-sec)] bg-[var(--hover-tint)] hover:bg-[var(--hover-tint)] transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            Back
          </button>
          
          <button
            onClick={onNext}
            disabled={!canProceed}
            className="flex items-center gap-2 px-6 py-3 bg-[var(--slate)] text-white hover:bg-[var(--slate)] disabled:bg-[var(--surface)] disabled:text-[var(--text-ter)] disabled:cursor-not-allowed transition-colors font-medium"
          >
            {phase === 'costOfDelay' ? 'Next: Job Duration' : 'Calculate WSJF'}
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
}


